import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { ApiError } from "../lib/api";
import { type PastedBand, pasteConcerts, previewConcerts, queryKeys } from "./api";
import { useDebounced } from "./use-debounced";

/** L'attente apres la derniere frappe avant de demander l'apercu, en ms. */
export const PREVIEW_DELAY_MS = 400;

const plural = (count: number, one: string, many: string) =>
  `${String(count)} ${count > 1 ? many : one}`;

/**
 * « Coller mes concerts » (planche « Reglages », spec section 3.7 ; ticket 6, #513). Le texte part
 * au serveur pour l'apercu (`POST /api/t/concerts/coller` sans `selected`), une fois la frappe
 * finie : « N groupes reconnus, dont M deja dans ta frise », puis la liste des nouveaux, tous
 * coches. « Ajouter K » cree un point « a dater » par groupe coche ; l'apercu se relit ensuite, et
 * les groupes ajoutes y passent en « deja dans ta frise ».
 */
export function ConcertsPaste() {
  const id = useId();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const debounced = useDebounced(text, PREVIEW_DELAY_MS);
  const ready = debounced.trim() !== "";
  // Les groupes decoches, par cle : un nouveau groupe arrive coche.
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  const [done, setDone] = useState<{ text: string; added: number } | null>(null);

  const preview = useQuery({
    queryKey: queryKeys.concerts(debounced),
    queryFn: () => previewConcerts(debounced),
    enabled: ready,
    retry: false,
  });

  const fresh: PastedBand[] = preview.data?.bands.filter((band) => !band.alreadyThere) ?? [];
  const selected = fresh.filter((band) => !unchecked.has(band.key)).map((band) => band.name);

  const add = useMutation({
    mutationFn: () => pasteConcerts(debounced, selected),
    onSuccess: (result) => {
      setDone({ text: debounced, added: result.added });
      void queryClient.invalidateQueries({ queryKey: ["t", "concerts"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.frise });
      void queryClient.invalidateQueries({ queryKey: queryKeys.questions });
    },
  });

  const toggle = (key: string, checked: boolean) => {
    setUnchecked((current) => {
      const next = new Set(current);
      if (checked) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const data = ready ? preview.data : undefined;

  return (
    <section aria-labelledby={`${id}-titre`} className="flex max-w-md flex-col gap-2">
      <h2 className="m-0 text-[15px] font-semibold" id={`${id}-titre`}>
        Coller mes concerts
      </h2>
      <textarea
        aria-describedby={`${id}-aide`}
        aria-labelledby={`${id}-titre`}
        className="min-h-24 resize-y rounded-[10px] border border-input bg-tl-field px-3 py-2.5 font-[inherit] text-base text-foreground"
        onChange={(event) => {
          setText(event.target.value);
          setDone(null);
          add.reset();
        }}
        placeholder={"- Tryo\n- Noir Désir x2\n- -M-"}
        rows={4}
        value={text}
      />
      <div aria-live="polite" className="flex flex-col gap-2">
        {ready && preview.isError ? (
          <p className="m-0 text-sm text-destructive" role="alert">
            {preview.error instanceof ApiError
              ? preview.error.message
              : "La lecture n'a pas abouti. Réessaie."}
          </p>
        ) : null}
        {data ? (
          <div className="flex items-center justify-between gap-2.5 rounded-[10px] bg-muted px-3 py-2.5 text-sm">
            <span>
              {data.recognized === 0 ? (
                "Aucun groupe reconnu."
              ) : (
                <>
                  <strong>{plural(data.recognized, "groupe", "groupes")}</strong>{" "}
                  {data.recognized > 1 ? "reconnus" : "reconnu"}, dont {data.alreadyThere} déjà dans
                  ta frise
                </>
              )}
            </span>
            {fresh.length > 0 ? (
              <button
                className="min-h-10 whitespace-nowrap rounded-lg bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                disabled={selected.length === 0 || add.isPending}
                onClick={() => add.mutate()}
                type="button"
              >
                {add.isPending ? "Ajout..." : `Ajouter ${String(selected.length)}`}
              </button>
            ) : null}
          </div>
        ) : null}
        {data?.truncated ? (
          <p className="m-0 text-xs text-muted-foreground">
            Seuls les 300 premiers groupes sont lus.
          </p>
        ) : null}
        {done !== null && done.text === debounced ? (
          <p className="m-0 text-sm text-tl-ok">
            {done.added === 0
              ? "Rien de nouveau : ils sont déjà tous dans ta frise."
              : `${plural(done.added, "concert ajouté", "concerts ajoutés")} à ta frise, à dater.`}
          </p>
        ) : null}
        {add.isError ? (
          <p className="m-0 text-sm text-destructive" role="alert">
            {add.error instanceof ApiError
              ? add.error.message
              : "L'ajout n'a pas abouti. Réessaie."}
          </p>
        ) : null}
      </div>
      {data && fresh.length > 0 ? (
        <fieldset className="m-0 flex max-h-72 flex-col overflow-y-auto rounded-[10px] border border-border px-3 py-1">
          <legend className="sr-only">Les groupes à ajouter</legend>
          {fresh.map((band) => (
            <label className="flex min-h-11 items-center gap-2.5 text-[15px]" key={band.key}>
              <input
                checked={!unchecked.has(band.key)}
                className="size-5 accent-[#F3EEE3]"
                onChange={(event) => toggle(band.key, event.target.checked)}
                type="checkbox"
              />
              <span>
                {band.name}
                {band.count > 1 ? (
                  <span className="text-muted-foreground"> · vu {band.count} fois</span>
                ) : null}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}
      <p className="m-0 text-xs text-muted-foreground" id={`${id}-aide`}>
        Le même brouillon que sur « Line-up de ma vie », un groupe par ligne. Ils arrivent « à dater
        », les questions t'aideront.
      </p>
    </section>
  );
}
